<template>
    <div class="modal fade bg-secondary bg-opacity-75" id="setupRecurringServices" tabindex="-1" role="dialog" data-bs-backdrop="static" data-bs-keyboard="false">
        <div class="modal-dialog modal-xl">
            <div class="modal-content">
                <div class="modal-header py-2">
                    <h5 class="modal-title">Setup Recurring Services</h5>
                    <button type="button" class="btn-close" @click="closeModal"></button>
                </div>
                <div class="modal-body">
                    <div class="alert alert-primary">
                        <strong>Setup Recurring Services: Create an Automated Schedule</strong>
                        <br>
                        Use this form to define an automated, repeating schedule for the selected service (Light Installation And Repair). Simply select how often the service should occur and its start date.
                        <br>
                        <ul>
                            <li><strong>Frequency Type:</strong> Choose the cycle (e.g., Monthly, Weekly, Annually).</li>
                            <li><strong>Interval:</strong> Specify how many cycles between each service (e.g., Every 1 month).</li>
                            <li><strong>Start Date:</strong> The first date the recurring service will be generated.</li>
                            <li><strong>Max Occurrences / End Date:</strong>Optionally set a limit for the schedule or leave empty for an ongoing service.</li>
                        </ul>
                    </div>
                    <!-- Display section to select property and service -->
                    <div class="card">
                        <div class="card-body">
                            <div v-if="!canSetupServices">
                                <div class="alert alert-primary d-flex align-items-center p-2" role="alert">
                                    <Info :size="16" class="me-1"/>
                                    <div>{{ addServiceNote }}</div>
                                </div>
                                <div class="row g-3 py-4">
                                    <!-- If customerId is empty -->
                                    <div v-if="!customerId" class="col-md-4">
                                        <div class="form-group">
                                            <label class="form-label">Select customer <span class="text-danger">*</span></label>
                                            <vue-multiselect
                                                v-model="selectedCustomer"
                                                :options="customerOptions"
                                                :placeholder="'Choose a customer'"
                                                :show-labels="false"
                                                track-by="id"
                                                label="name"
                                                :multiple="false"
                                                @select="selectCustomer"
                                            ></vue-multiselect>
                                        </div>
                                    </div>
                                    <div :class="!customerId ? 'col-md-4' : 'col-md-6'">
                                        <div class="form-group">
                                            <label class="form-label">Select property <span class="text-danger">*</span></label>
                                            <vue-multiselect
                                                v-model="selectedProperty"
                                                :options="propertyOptions"
                                                :placeholder="'Choose a property'"
                                                :show-labels="false"
                                                track-by="id"
                                                label="name"
                                                :multiple="false"
                                                @select="selectProperty"
                                            ></vue-multiselect>
                                        </div>
                                    </div>
                                    <div :class="!customerId ? 'col-md-4' : 'col-md-6'">
                                        <div class="form-group">
                                            <label class="form-label">Select service <span class="text-danger">*</span></label>
                                            <vue-multiselect
                                                v-model="selectedService"
                                                :options="serviceOptions"
                                                :placeholder="'Choose a service to schedule'"
                                                :show-labels="false"
                                                track-by="id"
                                                label="name"
                                                :multiple="false"
                                                @select="selectService"
                                            ></vue-multiselect>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div v-else-if="recurringServicesData.length <= 0">
                                <div class="alert alert-primary d-flex align-items-center p-2" role="alert">
                                    <Info :size="16" class="me-1"/>
                                    <div>{{ addServiceNote }}</div>
                                </div>
                                <div class="row g-3 py-4">
                                    <div class="col-md-6">
                                        <div class="form-group">
                                            <label class="form-label">Property service <span class="text-danger">*</span></label>
                                            <vue-multiselect
                                                v-model="selectedService"
                                                :options="serviceOptions"
                                                :placeholder="'Choose a service to schedule'"
                                                :show-labels="false"
                                                track-by="id"
                                                label="name"
                                                :multiple="false"
                                                @select="selectService"
                                            ></vue-multiselect>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <template v-else-if="canSetupServices">
                                <ul class="nav nav-tabs nav-tabs-bordered" id="recurringServicesTab" role="tablist">
                                    <li
                                        v-for="(serviceData, index) in recurringServicesData"
                                        :key="index"
                                        class="nav-item"
                                        role="presentation"
                                    >
                                        <button
                                            class="nav-link"
                                            :class="{ 'active': index === 0 }"
                                            :id="`service-tab-${index}`"
                                            data-bs-toggle="tab"
                                            :data-bs-target="`#service-${index}`"
                                            type="button"
                                            role="tab"
                                            :aria-controls="`service-${index}`"
                                            :aria-selected="index === 0"
                                        >{{ capitalize(serviceData.service_name || '') }}</button>
                                    </li>

                                    <!-- Display service options -->
                                    <li v-if="canAddService && !isEditMode" class="nav-item ms-2" role="presentation">
                                        <vue-multiselect
                                            v-model="selectedService"
                                            :options="serviceOptions"
                                            :placeholder="'Choose a service to schedule'"
                                            :show-labels="false"
                                            track-by="id"
                                            label="name"
                                            :multiple="false"
                                            @select="selectService"
                                        ></vue-multiselect>
                                    </li>
                                </ul>
                                <div class="tab-content pt-2">
                                    <template v-if="recurringServicesData.length > 0">
                                        <div 
                                            v-for="(serviceData, index) in recurringServicesData"
                                            :key="index"
                                            :class="`tab-pane fade show ${index === 0 ? 'active' : ''}`"
                                            :id="`service-${index}`"
                                            role="tabpanel"
                                            :aria-labelledby="`service-${index}`"
                                        >
                                            <div v-if="serviceData.service_id > 0">
                                                <div class="row">
                                                    <div class="col-md-6">
                                                        <div class="form-group">
                                                            <label class="form-label">Frequency Type <span class="text-danger">*</span></label>
                                                            <vue-select
                                                                v-model="serviceData.frequency_type"
                                                                :items="frequencyOptions"
                                                                :placeholder="'Select Frequency'"
                                                                :searchable="false"
                                                                @change="onFrequencyTypeChange(serviceData)"
                                                            ></vue-select>
                                                        </div>
                                                    </div>
                                                    <div class="col-md-6">
                                                        <div class="form-group">
                                                            <label class="form-label">Interval <span class="text-danger">*</span></label>
                                                            <vue-select
                                                                v-model="serviceData.interval"
                                                                :items="getIntervalOptionsForService(serviceData.frequency_type)"
                                                                :placeholder="'Select Interval'"
                                                                :searchable="false"
                                                            ></vue-select>
                                                        </div>
                                                    </div>

                                                    <!-- Weekly Configuration -->
                                                    <div v-if="serviceData.frequency_type === 'weekly'" class="col-12">
                                                        <div class="form-group">
                                                            <label class="form-label">Repeat on Days <span class="text-danger">*</span></label>
                                                            <div class="days-selector">
                                                                <button
                                                                    v-for="day in daysOfWeek"
                                                                    :key="day.id"
                                                                    type="button"
                                                                    class="btn btn-outline-primary btn-sm day-btn"
                                                                    :class="{ 'active': isDaySelected(serviceData, day.id) }"
                                                                    @click="toggleDay(serviceData, day.id)"
                                                                >
                                                                    {{ day.name }}
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <!-- Monthly Configuration -->
                                                    <div v-if="serviceData.frequency_type === 'monthly'" class="col-12">
                                                        <div class="form-group">
                                                            <label class="form-label">Day of Month <span class="text-danger">*</span></label>
                                                            <vue-select
                                                                v-model="serviceData.monthly_day"
                                                                :items="monthlyDayOptions"
                                                                :placeholder="'Day of Month'"
                                                                :searchable="false"
                                                            ></vue-select>
                                                        </div>
                                                    </div>

                                                    <!-- Yearly Configuration -->
                                                    <template v-if="serviceData.frequency_type === 'yearly'">
                                                        <div class="col-md-6">
                                                            <label class="form-label">Month <span class="text-danger">*</span></label>
                                                            <vue-select
                                                                v-model="serviceData.yearly_month"
                                                                :items="months"
                                                                :placeholder="'Month'"
                                                                :searchable="false"
                                                            ></vue-select>
                                                        </div>
                                                        <div class="col-md-6">
                                                            <label class="form-label">Day of Month <span class="text-danger">*</span></label>
                                                            <vue-select
                                                                v-model="serviceData.yearly_day"
                                                                :items="monthlyDayOptions"
                                                                :placeholder="'Day of Month'"
                                                                :searchable="false"
                                                            ></vue-select>
                                                        </div>
                                                    </template>

                                                    <div class="col-md-6">
                                                        <div class="form-group">
                                                            <label class="form-label">Start Year <span class="text-danger">*</span></label>
                                                            <VueDatePicker
                                                                name="start_date"
                                                                v-model="serviceData.start_date"
                                                                year-picker
                                                                :format="'yyyy'"
                                                                :enable-time-picker="false"
                                                                :display-marker="false"
                                                                :clearable="false"
                                                                :timezone="'UTC'"
                                                                :min-date="new Date()"
                                                                :auto-apply="true"
                                                            ></VueDatePicker>
                                                        </div>
                                                    </div>
                                                    <div class="col-md-6">
                                                        <div class="form-group">
                                                            <label class="form-label">End Year</label>
                                                            <VueDatePicker
                                                                name="end_date"
                                                                v-model="serviceData.end_date"
                                                                year-picker
                                                                :format="'yyyy'"
                                                                :enable-time-picker="false"
                                                                :display-marker="false"
                                                                :clearable="false"
                                                                :timezone="'UTC'"
                                                                :min-date="serviceData.start_date ? new Date(serviceData.start_date, 0, 1) : null"
                                                                :auto-apply="true"
                                                            ></VueDatePicker>
                                                        </div>
                                                    </div>
                                                    <div class="col-md-6">
                                                        <div class="form-group">
                                                            <label class="form-label">Max Occurrences</label>
                                                            <input 
                                                                type="number" 
                                                                class="form-control" 
                                                                v-model="serviceData.max_occurrences"
                                                                min="1"
                                                                placeholder="Leave empty for unlimited"
                                                            />
                                                        </div>
                                                    </div>
                                                    <div class="col-md-6">
                                                        <div class="form-group">
                                                            <label class="form-label">Status</label>
                                                            <vue-select
                                                                v-model="serviceData.status"
                                                                :items="statusOptions"
                                                                :placeholder="'Select Status'"
                                                                :searchable="false"
                                                            ></vue-select>
                                                        </div>
                                                    </div>

                                                    <!-- Preview Section -->
                                                    <div class="col-12" v-if="serviceData.frequency_type">
                                                        <div class="preview-section">
                                                            <h6>Next 5 Scheduled Dates:</h6>
                                                            <div class="preview-dates">
                                                                <span 
                                                                    v-for="(date, dateIndex) in getPreviewDates(serviceData)" 
                                                                    :key="dateIndex"
                                                                    class="badge bg-primary me-2"
                                                                >
                                                                    {{ formatPreviewDate(date) }}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </template>
                                    <template v-else>
                                        <div class="tab-pane fade show active" role="tabpanel" aria-labelledby="service-new">
                                           <div class="row g-3">
                                                <div class="col-12">
                                                    <p>Please select a service to setup recurring services.</p>
                                                </div>
                                            </div>
                                        </div>
                                    </template>
                                </div>
                            </template>
                        </div>
                    </div>
                </div>
                <div v-if="canSetupServices" class="modal-footer">
                    <button type="button" class="btn btn-outline-danger btn-sm" @click="closeModal">Close</button>
                    <button 
                        type="button" 
                        class="btn btn-theme btn-sm" 
                        @click="saveRecurringServices"
                        :disabled="processing"
                    >
                        {{ processing ? 'Saving...' : 'Save' }}
                    </button>
                </div>
                
            </div>
        </div>
    </div>
</template>

<script>
import { appMixin } from "../../mixin/appMixin";
import VueSelect from "../partials/VueSelect.vue";
import { Info } from 'lucide-vue-next'

export default {
    name: "SetupRecurringServices",
    mixins: [appMixin],
    components: {
        VueSelect,
        Info
    },
    props: {
        orderId: {
            type: [String, Number],
            default: null
        },
        services: {
            type: [Array, Object],
            default: () => []
        },
        addressId: {
            type: [String, Number],
            default: null
        },
        customerId: {
            type: [String, Number],
            default: null
        },
        source: {
            type: String,
            default: 'scheduled-services'
        },
        canAddService: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            processing: false,
            recurringServicesData: [],
            daysOfWeek: [
                { id: 'monday', name: 'Monday', dayNumber: 1 },
                { id: 'tuesday', name: 'Tuesday', dayNumber: 2 },
                { id: 'wednesday', name: 'Wednesday', dayNumber: 3 },
                { id: 'thursday', name: 'Thursday', dayNumber: 4 },
                { id: 'friday', name: 'Friday', dayNumber: 5 },
                { id: 'saturday', name: 'Saturday', dayNumber: 6 },
                { id: 'sunday', name: 'Sunday', dayNumber: 0 }
            ],
            months: [
                { id: 1, name: 'January' },
                { id: 2, name: 'February' },
                { id: 3, name: 'March' },
                { id: 4, name: 'April' },
                { id: 5, name: 'May' },
                { id: 6, name: 'June' },
                { id: 7, name: 'July' },
                { id: 8, name: 'August' },
                { id: 9, name: 'September' },
                { id: 10, name: 'October' },
                { id: 11, name: 'November' },
                { id: 12, name: 'December' }
            ],
            scheduledServices: [],
            serviceList: [], // Available services in the database
            selectedNewService: '',
            selectedService: null, // This will handle the selected object
            selectedServiceId: '',
            selectedProperty: null, // This will handle the selected object
            selectedPropertyId: this.addressId,
            selectedCustomerId: this.customerId,
            availableProperties: [],
            customers: [],
            selectedCustomer: null,
            isEditMode: false
        }
    },
    mounted() {
        this.getScheduledServices();

        if (this.canAddService) {
            this.getServices();
        }

        // Check selected property id is empty then get available properties
        if (!this.selectedPropertyId && this.selectedCustomerId) {
            this.getAvailableProperties();
        }

        // Check if customer is not available then get customers
        if (!this.selectedCustomerId) {
            this.getCustomers();
        }

        // Handel edit schedules click button
        $('body').on('click', '.edit-scheduled-service', (event) => {
            event.preventDefault();

            // collect the data attributes
            const $el = $(event.currentTarget);  // Use event.currentTarget instead of $(this)
            const scheduledServiceId = $el.data('scheduled-service-id');
            const customerId = $el.data('customer-id');
            const addressId = $el.data('address-id');
            const serviceId = $el.data('service-id');

            // Edit the scheduled service
            this.editScheduledService({
                id: scheduledServiceId,
                customer_id: customerId,
                address_id: addressId,
                service_id: serviceId
            });
        });
    },
    computed: {
        frequencyOptions() {
            return [
                {id: 'daily', text: 'Daily', name: 'Daily'},
                {id: 'weekly', text: 'Weekly', name: 'Weekly'},
                {id: 'monthly', text: 'Monthly', name: 'Monthly'},
                {id: 'yearly', text: 'Yearly', name: 'Yearly'},
            ];
        },
        statusOptions() {
            return [
                {id: 'active', text: 'Active', name: 'Active'},
                {id: 'inactive', text: 'Inactive', name: 'Inactive'},
                {id: 'paused', text: 'Paused', name: 'Paused'},
                {id: 'cancelled', text: 'Cancelled', name: 'Cancelled'},
                {id: 'completed', text: 'Completed', name: 'Completed'},
            ];
        },
        monthlyDayOptions() {
            const options = [];
            for (let i = 1; i <= 31; i++) {
                options.push({
                    id: i,
                    text: `Day ${i}`,
                    name: `Day ${i}`
                });
            }
            options.push({
                id: 'last',
                text: 'Last Day of Month',
                name: 'Last Day of Month'
            });
            return options;
        },
        recurringServicesSummary() {
            return this.scheduledServices
                .filter(service => service.service_id > 0 && service.frequency_type)
                .map(service => {
                    const previewDates = this.getPreviewDates(service);
                    const nextDate = previewDates.length > 0 ? previewDates[0] : null;
                    
                    // Format frequency text
                    const frequencyText = this.getFrequencyText(service);
                    
                    return {
                        id: service.id,
                        service_name: service.service_name,
                        frequency: frequencyText,
                        next_date: nextDate,
                        formatted_next_date: nextDate ? this.moment(nextDate).format('DD/MM/YYYY') : 'N/A',
                        service_id: service.service_id,
                        status: service.status
                    };
                });
        },
        serviceOptions() {
            // Get service IDs from both recurringServicesData and scheduledServices
            const recurringServiceIds = this.recurringServicesData
                .filter(service => service.address_id == this.selectedPropertyId)
                .map(s => s.service_id);
            const scheduledServiceIds = this.scheduledServices
                .filter(service => service.address_id == this.selectedPropertyId)
                .map(s => s.service_id);
            
            // Combine both arrays to get all selected/scheduled service IDs
            const disabledServiceIds = [...new Set([...recurringServiceIds, ...scheduledServiceIds])];
            
            return this.serviceList.map(service => ({
                id: service.id,
                text: service.name,
                name: service.name,
                $isDisabled: disabledServiceIds.includes(service.id)
            }));
        },
        // Prepare options for the properties
        propertyOptions() {
            return this.availableProperties.map(property => ({
                id: property.id,
                text: property.full_address,
                name: property.full_address,
            }));
        },
        /**
         * Check if user can setup services
         * If addressId prop exists → show form immediately (return true)
         * If addressId is null → require property and customer selection first
         */
        canSetupServices() {
            if(this.isEditMode) {
                return true;
            }
            // If address ID is provided as prop, show the form immediately
            if (this.addressId) {
                return true;
            }
            
            // If no address ID prop, check if user has selected both property and customer
            return this.selectedPropertyId && this.selectedServiceId && this.selectedCustomerId;
        },
        addServiceNote() {
            if(!this.selectedCustomerId) {
                return 'To get started, please select a customer from the list, choose a property address, and pick a service to schedule';
            }
            else if(!this.selectedPropertyId) {
                return 'To get started, please select a property address, and pick a service to schedule';
            }
            else if(!this.selectedServiceId) {
                return 'To get started, please pick a service to schedule';
            }
        },
        customerOptions() {
            return this.customers.map(customer => ({
                id: customer.id,
                text: customer.name,
                name: customer.name,
            }));
        }
    },
    watch: {
        services: {
            handler(newServices) {
                this.initializeRecurringServicesData();
            },
            immediate: true,
            deep: true
        }
    },
    methods: {
        openModal() {
           $('#setupRecurringServices').modal('show'); 
        },
        closeModal() {
            $('#setupRecurringServices').modal('hide');
            this.isEditMode = false;

            // Reset the selectedCustomerID, selectedPropertyId and selectedServiceId
            if(this.source === 'scheduled-services') {
                this.selectedCustomerId = null;
                this.selectedCustomer = null;
                this.selectedPropertyId = null;
                this.selectedProperty = null;

                // Reset the recurringServicesData
                this.recurringServicesData = [];
            }
            else if(['address-detail', 'customer-scheduled-services'].includes(this.source)) {
                 this.selectedPropertyId = null;
                this.selectedProperty = null;

                // Reset the recurringServicesData
                this.recurringServicesData = [];
            }
            
            this.selectedServiceId = null;
            this.selectedService = null;
        },
        getServices() {
            axios.post('/api/admin/services')
                .then(response => {
                    this.serviceList = response.data.services || [];
                })
                .catch(error => {
                    console.error(error);
                });
        },
        initializeRecurringServicesData() {
            this.recurringServicesData = this.services
                .filter(service => service.service_id > 0)
                .map(service => ({
                    customer_id: this.selectedCustomerId || '',
                    order_id: this.orderId || '',
                    address_id: this.selectedPropertyId || '',
                    service_id: service.service_id || '',
                    service_name: service.service_name || '',
                    price: service.price || 0,
                    labour_cost: service.labour_cost || 0,
                    frequency_type: '',
                    interval: 1,
                    start_date: this.getTodayDate(),
                    end_date: null,
                    max_occurrences: null,
                    status: 'active',
                    frequency_details: {},
                    // Weekly
                    selected_days: ['monday'], // Default to Monday
                    // Monthly
                    monthly_day: 1,
                    // Yearly
                    yearly_month: 1, // January
                    yearly_day: 1
                }));
        },
        onFrequencyTypeChange(serviceData) {
            // Reset frequency-specific fields when frequency type changes
            serviceData.selected_days = ['monday'];
            serviceData.monthly_day = 1;
            serviceData.yearly_month = 1;
            serviceData.yearly_day = 1;
        },
        getIntervalOptionsForService(frequencyType) {
            if (!frequencyType) {
                return [];
            }
            const options = [];
            let maxInterval = 12;
            let label = '';
            switch(frequencyType) {
                case 'daily':
                    maxInterval = 30;
                    label = 'day';
                    break;
                case 'weekly':
                    maxInterval = 12;
                    label = 'week';
                    break;
                case 'monthly':
                    maxInterval = 12;
                    label = 'month';
                    break;
                case 'yearly':
                    maxInterval = 5;
                    label = 'year';
                    break;
            }

            for (let i = 1; i <= maxInterval; i++) {
                const pluralLabel = i > 1 ? `${label}s` : label;
                options.push({
                    id: i,
                    text: `Every ${i} ${pluralLabel}`,
                    name: `Every ${i} ${pluralLabel}`
                });
            }
            return options;
        },
        isDaySelected(serviceData, day) {
            return serviceData.selected_days.includes(day);
        },
        toggleDay(serviceData, day) {
            const index = serviceData.selected_days.indexOf(day);
            if (index > -1) {
                serviceData.selected_days.splice(index, 1);
            } else {
                serviceData.selected_days.push(day);
            }
        },
        getPreviewDates(serviceData) {
            if (!serviceData.start_date || !serviceData.frequency_type) {
                return [];
            }

            const dates = [];
            // Convert year-picker format to Date object (first day of year)
            let currentDate;
            if (typeof serviceData.start_date === 'number') {
                // Year-picker returns just a year number
                currentDate = new Date(serviceData.start_date, 0, 1);
            } else {
                currentDate = new Date(serviceData.start_date);
            }
            
            // Get the first valid date based on frequency settings
            let firstDate = this.getFirstValidDate(currentDate, serviceData);
            if (!firstDate) return [];
            
            dates.push(firstDate);
            
            // Calculate next 4 dates
            for (let i = 1; i < 5; i++) {
                const nextDate = this.calculateNextDate(new Date(dates[i-1]), serviceData);
                if (nextDate) {
                    dates.push(nextDate);
                }
            }

            return dates;
        },
        getFirstValidDate(startDate, serviceData) {
            const start = new Date(startDate);
            
            switch(serviceData.frequency_type) {
                case 'daily':
                    return start;
                    
                case 'weekly':
                    if (serviceData.selected_days && serviceData.selected_days.length > 0) {
                        return this.findNextWeeklyDate(start, serviceData, true);
                    }
                    return start;
                    
                case 'monthly':
                    if (serviceData.monthly_day) {
                        return this.findFirstMonthlyDate(start, serviceData);
                    }
                    return start;
                    
                case 'yearly':
                    if (serviceData.yearly_month && serviceData.yearly_day) {
                        return this.findFirstYearlyDate(start, serviceData);
                    }
                    return start;
                    
                default:
                    return start;
            }
        },
        
        calculateNextDate(currentDate, serviceData) {
            const nextDate = new Date(currentDate);
            const interval = serviceData.interval || 1;

            switch(serviceData.frequency_type) {
                case 'daily':
                    nextDate.setDate(nextDate.getDate() + interval);
                    return nextDate;
                    
                case 'weekly':
                    if (serviceData.selected_days && serviceData.selected_days.length > 0) {
                        return this.findNextWeeklyDate(nextDate, serviceData);
                    } else {
                        nextDate.setDate(nextDate.getDate() + (7 * interval));
                        return nextDate;
                    }
                    
                case 'monthly':
                    if (serviceData.monthly_day) {
                        return this.findNextMonthlyDate(nextDate, serviceData);
                    } else {
                        nextDate.setMonth(nextDate.getMonth() + interval);
                        return nextDate;
                    }
                    
                case 'yearly':
                    if (serviceData.yearly_month && serviceData.yearly_day) {
                        return this.findNextYearlyDate(nextDate, serviceData);
                    } else {
                        nextDate.setFullYear(nextDate.getFullYear() + interval);
                        return nextDate;
                    }
                default:
                    return nextDate;
            }
        },

        findFirstMonthlyDate(startDate, serviceData) {
            const start = new Date(startDate);
            let currentDate = new Date(start);
            
            // Check current month first
            let candidateDate = this.getMonthlyDateForMonth(currentDate.getFullYear(), currentDate.getMonth(), serviceData.monthly_day);
            
            // If the candidate date is in the future or today, use it
            if (candidateDate >= start) {
                return candidateDate;
            }
            
            // Otherwise, go to next month
            currentDate.setMonth(currentDate.getMonth() + 1);
            candidateDate = this.getMonthlyDateForMonth(currentDate.getFullYear(), currentDate.getMonth(), serviceData.monthly_day);
            
            return candidateDate;
        },
        
        findFirstYearlyDate(startDate, serviceData) {
            const start = new Date(startDate);
            let currentDate = new Date(start);
            
            // Check current year first
            let candidateDate = this.getYearlyDateForYear(currentDate.getFullYear(), serviceData);
            
            // If the candidate date is in the future or today, use it
            if (candidateDate >= start) {
                return candidateDate;
            }
            
            // Otherwise, go to next year
            currentDate.setFullYear(currentDate.getFullYear() + 1);
            candidateDate = this.getYearlyDateForYear(currentDate.getFullYear(), serviceData);
            
            return candidateDate;
        },
        
        findNextWeeklyDate(startDate, serviceData, isFirstDate = false) {
            let currentDate = new Date(startDate);
            const selectedDayNumbers = this.getSelectedDayNumbers(serviceData.selected_days);
            
            if (!isFirstDate) {
                // For subsequent dates, start from the next day
                currentDate.setDate(currentDate.getDate() + 1);
            }
            
            // Look for the next matching day within a reasonable timeframe
            for (let i = 0; i < 7 * (serviceData.interval || 1) * 2; i++) {
                if (selectedDayNumbers.includes(currentDate.getDay())) {
                    return new Date(currentDate);
                }
                currentDate.setDate(currentDate.getDate() + 1);
            }
            
            return currentDate;
        },
        
        findNextMonthlyDate(currentDate, serviceData) {
            let nextDate = new Date(currentDate);
            nextDate.setMonth(nextDate.getMonth() + (serviceData.interval || 1));
            
            return this.getMonthlyDateForMonth(nextDate.getFullYear(), nextDate.getMonth(), serviceData.monthly_day);
        },
        
        findNextYearlyDate(currentDate, serviceData) {
            let nextDate = new Date(currentDate);
            nextDate.setFullYear(nextDate.getFullYear() + (serviceData.interval || 1));
            
            return this.getYearlyDateForYear(nextDate.getFullYear(), serviceData);
        },
        
        getMonthlyDateForMonth(year, month, monthlyDay) {
            if (monthlyDay === 'last') {
                // Last day of month
                return new Date(year, month + 1, 0);
            } else {
                // Specific day of month
                const day = parseInt(monthlyDay);
                const daysInMonth = new Date(year, month + 1, 0).getDate();
                const actualDay = Math.min(day, daysInMonth);
                return new Date(year, month, actualDay);
            }
        },
        
        getYearlyDateForYear(year, serviceData) {
            const month = serviceData.yearly_month - 1; // Convert to 0-indexed
            
            if (serviceData.yearly_day === 'last') {
                // Last day of month
                return new Date(year, month + 1, 0);
            } else {
                // Specific day of month
                const day = parseInt(serviceData.yearly_day);
                const daysInMonth = new Date(year, month + 1, 0).getDate();
                const actualDay = Math.min(day, daysInMonth);
                return new Date(year, month, actualDay);
            }
        },
        
        getSelectedDayNumbers(selectedDays) {
            return selectedDays.map(dayId => {
                const day = this.daysOfWeek.find(d => d.id === dayId);
                return day ? day.dayNumber : 1; // Default to Monday if not found
            });
        },
        
        doesDateMatchCriteria(date, serviceData) {
            if (serviceData.frequency_type === 'weekly') {
                const dayNumber = date.getDay();
                const selectedDayNumbers = this.getSelectedDayNumbers(serviceData.selected_days);
                return selectedDayNumbers.includes(dayNumber);
            }
            return true;
        },
        formatPreviewDate(date) {
            return date.toLocaleDateString('en-GB', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            });
        },
        getTodayDate() {
            // Return current year for year-picker
            return new Date().getFullYear();
        },
        getDefaultStartDate(frequencyType, serviceData) {
            // Return current year for year-picker
            return new Date().getFullYear();
        },
        formatYearPickerToDate(year, isEndDate = false) {
            // Convert year number to date string
            if (!year) return null;
            
            if (isEndDate) {
                // Last day of the year (December 31)
                return `${year}-12-31`;
            } else {
                // First day of the year (January 1)
                return `${year}-01-01`;
            }
        },
        parseYearFromDate(dateString) {
            // Convert date string to year number for year-picker
            if (!dateString) return null;
            const date = new Date(dateString);
            return date.getFullYear();
        },
        validateRecurringServices() {
            for (let i = 0; i < this.recurringServicesData.length; i++) {
                const service = this.recurringServicesData[i];
                
                if (!service.frequency_type) {
                    this.$notification('error', `Please select frequency type for ${service.service_name}`);
                    return false;
                }
                
                if (!service.interval || service.interval < 1) {
                    this.$notification('error', `Please enter valid interval for ${service.service_name}`);
                    return false;
                }

                // Weekly validation
                if (service.frequency_type === 'weekly' && (!service.selected_days || service.selected_days.length === 0)) {
                    this.$notification('error', `Please select at least one day for weekly frequency for ${service.service_name}`);
                    return false;
                }

                // Monthly validation
                if (service.frequency_type === 'monthly' && !service.monthly_day) {
                    this.$notification('error', `Please select a day for monthly frequency for ${service.service_name}`);
                    return false;
                }

                // Yearly validation
                if (service.frequency_type === 'yearly' && (service.yearly_month === undefined || !service.yearly_day)) {
                    this.$notification('error', `Please select month and day for yearly frequency for ${service.service_name}`);
                    return false;
                }
                
                if (!service.start_date) {
                    this.$notification('error', `Please select start year for ${service.service_name}`);
                    return false;
                }

                // Check if end_date and max_occurrences are both empty
                if (!service.end_date && !service.max_occurrences) {
                    this.$notification('error', `Please select end year or max occurrences for ${service.service_name}`);
                    return false;
                }
            }
            
            return true;
        },
        async saveRecurringServices() {
            if (!this.validateRecurringServices()) {
                return;
            }
            
            this.processing = true;
            
            try {
                // Prepare frequency_details object
                const payload = {
                    order_id: this.orderId,
                    address_id: this.selectedPropertyId,
                    customer_id: this.selectedCustomerId,
                    services: this.recurringServicesData.map(service => {
                        const frequencyDetails = {};
                        
                        if (service.frequency_type === 'weekly') {
                            frequencyDetails.week_days = service.selected_days;
                        } else if (service.frequency_type === 'monthly') {
                            frequencyDetails.monthly_day = service.monthly_day;
                        } else if (service.frequency_type === 'yearly') {
                            frequencyDetails.yearly_month = service.yearly_month;
                            frequencyDetails.yearly_day = service.yearly_day;
                        }
                        
                        return {
                            service_id: service.service_id,
                            frequency_type: service.frequency_type,
                            interval: service.interval,
                            start_date: this.formatYearPickerToDate(service.start_date, false),
                            end_date: this.formatYearPickerToDate(service.end_date, true),
                            max_occurrences: service.max_occurrences,
                            status: service.status,
                            frequency_details: frequencyDetails
                        }
                    })
                };
                
                const response = await axios.post('/api/admin/recurring-services/setup', payload);
                
                if (response.data.status === 'success') {
                    this.$notification('success', response.data.message || 'Recurring services setup successfully');
                    this.closeModal();
                    this.getScheduledServices();

                    // Trigger jQuery event
                    window.dispatchEvent(new CustomEvent('recurring-services-saved', {detail: {}}));
                    
                } else {
                    this.$notification('error', response.data.message || 'Failed to setup recurring services');
                }
            } catch (error) {
                console.error('Error saving recurring services:', error);
                this.$notification('error', error.response?.data?.message || 'An error occurred while saving recurring services');
            } finally {
                this.processing = false;
            }
        },
        getFrequencyText(serviceData) {
            if (!serviceData.frequency_type) return '';
            
            const interval = serviceData.interval || 1;
            let baseText = '';
            
            switch(serviceData.frequency_type) {
                case 'daily':
                    baseText = interval === 1 ? 'Every day' : `Every ${interval} days`;
                    break;
                case 'weekly':
                    if (serviceData.selected_days && serviceData.selected_days.length > 0) {
                        const dayNames = serviceData.selected_days.map(dayId => {
                            const day = this.daysOfWeek.find(d => d.id === dayId);
                            return day ? day.name : dayId;
                        });
                        baseText = `Every ${interval} week${interval > 1 ? 's' : ''} on ${dayNames.join(', ')}`;
                    } else {
                        baseText = `Every ${interval} week${interval > 1 ? 's' : ''}`;
                    }
                    break;
                case 'monthly':
                    const dayText = serviceData.monthly_day === 'last' ? 
                        'last day' : 
                        `day ${serviceData.monthly_day}`;
                    baseText = `Every ${interval} month${interval > 1 ? 's' : ''} on ${dayText}`;
                    break;
                case 'yearly':
                    const month = this.months.find(m => m.id === serviceData.yearly_month);
                    const monthName = month ? month.name : 'Unknown';
                    const yearlyDayText = serviceData.yearly_day === 'last' ? 
                        'last day' : 
                        `day ${serviceData.yearly_day}`;
                    baseText = `Every ${interval} year${interval > 1 ? 's' : ''} on ${monthName} ${yearlyDayText}`;
                    break;
                default:
                    baseText = serviceData.frequency_type;
            }
            
            return baseText;
        },
        // Get scheuled services
        async getScheduledServices(params = {}) {
            try {
                const response = await axios.post('/api/admin/scheduled-services/list', {
                    id: params.id ?? '',
                    service_id: params.service_id ?? '',
                    address_id: params.address_id ?? this.selectedPropertyId,
                    customer_id: params.customer_id ?? this.selectedCustomerId
                });
                
                if (response.data.status === 'success') {
                    //return response.data.scheduled_services || [];
                    if(response.data.scheduled_services.length > 0) {
                        this.scheduledServices = response.data.scheduled_services
                            .map(service => ({
                                id: service.setting_id || service.id || '',
                                customer_id: service.customer_id || '',
                                order_id: this.orderId || '',
                                address_id: service.address_id || '',
                                service_id: service.service_id || '',
                                service_name: service.service.name || '',
                                price: service.service.price || 0,
                                labour_cost: service.service.labour_cost || 0,
                                frequency_type: service.frequency_type || '',
                                interval: service.interval || 1,
                                start_date: this.parseYearFromDate(service.start_date),
                                end_date: this.parseYearFromDate(service.end_date),
                                max_occurrences: service.max_occurrences || null,
                                status: 'active',
                                frequency_details: {},
                                // Weekly
                                selected_days: service.frequency_details?.week_days || ['monday'],
                                // Monthly
                                monthly_day: service.frequency_details?.monthly_day || 1,
                                // Yearly
                                yearly_month: service.frequency_details?.yearly_month || 1,
                                yearly_day: service.frequency_details?.yearly_day || 1
                            }));

                        // Emit the scheduled services to parent
                        this.$emit('services-fetched', this.recurringServicesSummary);
                    }
                } else {
                    console.error('Error fetching scheduled services:', response.data.message);
                    this.$emit('services-fetched', []);
                    return [];
                }
            } catch (error) {
                console.error('Error fetching scheduled services:', error);
                this.$emit('services-fetched', []);
                return [];
            }
        },
        selectProperty(selected) {
            this.selectedPropertyId = selected.id || '';
        },
        selectService(selected) {
            this.selectedServiceId = selected.id || '';

            // Prepare recurrign service object with selected service
            if(selected.id) {
                this.recurringServicesData.push({
                    service_id: selected.id,
                    service_name: selected.name,
                    frequency_type: '',
                    interval: 1,
                    start_date: this.getTodayDate(),
                    end_date: null,
                    max_occurrences: null,
                    status: 'active',
                    frequency_details: {},
                    selected_days: ['monday'],
                    monthly_day: null,
                    yearly_month: null,
                    yearly_day: null
                });
            }

            // Set selected object null
            this.selectedService = null;
        },
        getAvailableProperties() {
            // Check if address is not empty
            if (this.selectedPropertyId && !this.selectedCustomerId) {
                return;
            }

            // Get the available properties
            try {
                axios.post('/api/admin/properties', {
                    customer_id: this.selectedCustomerId
                })
                .then(response => {
                    this.availableProperties = response.data.properties || [];
                })
                .catch(error => {
                    console.error('Error fetching available properties:', error);
                    this.availableProperties = [];
                });
            } catch (error) {
                console.error('Error fetching available properties:', error);
                this.availableProperties = [];
            }
        },
        getCustomers() {
            // Check if customer is not empty
            if (this.selectedCustomerId) {
                return;
            }

            // Get the customers
            try {
                axios.post('/api/admin/customers')
                .then(response => {
                    this.customers = response.data.customers || [];
                })
                .catch(error => {
                    console.error('Error fetching customers:', error);
                    this.customers = [];
                });
            } catch (error) {
                console.error('Error fetching customers:', error);
                this.customers = [];
            }
        },
        selectCustomer(selected) {
            if(selected.id) {
                this.selectedCustomerId = selected.id;
                this.getAvailableProperties();
            }
        },
        async editScheduledService(params) {

            await this.getScheduledServices(params);

            // Find the specific service to edit
            const serviceToEdit = this.scheduledServices.find(
                service => String(service.id) === String(params.id)
            );
            
            if (serviceToEdit) {
                this.isEditMode = true;
                // Mark address and customer is selected
                this.selectedPropertyId = serviceToEdit.address_id;
                this.selectedCustomerId = serviceToEdit.customer_id;

                // Do something with the service data
                this.recurringServicesData = [serviceToEdit];

                // Display the modal popup
                this.openModal();
            }
        }
    }
}
</script>

<style scoped>
.days-selector {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
}

.day-btn {
    min-width: 100px;
}

.day-btn.active {
    background-color: #007bff;
    color: white;
    border-color: #007bff;
}

.preview-section {
    background-color: #f8f9fa;
    padding: 15px;
    border-radius: 5px;
    border-left: 4px solid #007bff;
}

.preview-dates {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 8px;
}

.form-check {
    margin-bottom: 10px;
}
</style>